import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { FileText, ChevronRight, Download, CalendarCheck, CalendarPlus, PhoneCall, ChevronDown, Phone, MessageCircle, Stethoscope, Pencil, Users, Sparkles } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import AppointmentFormModal from '../components/AppointmentFormModal'
import { cn } from '../lib/utils'
import DictateButton from '../components/DictateButton'

// Lifecycle tracker colours (Paul 2026-06-07): green = accepted / treatment done,
// blue = patient has booked the appointment in the diary, yellow = outstanding.
const LIFECYCLE = {
  green:  { dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Done' },
  blue:   { dot: 'bg-blue-500',    pill: 'bg-blue-50 text-blue-700 border-blue-200',          label: 'Booked' },
  yellow: { dot: 'bg-amber-400',   pill: 'bg-amber-50 text-amber-800 border-amber-200',       label: 'Outstanding' },
  grey:   { dot: 'bg-violet-400',  pill: 'bg-violet-50 text-violet-700 border-violet-200',    label: 'Contact later' },
  slate:  { dot: 'bg-slate-400',   pill: 'bg-slate-100 text-slate-600 border-slate-200',      label: 'Blocked' },
}
const rand = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const randK = (n) => { n = n || 0; return n >= 1e6 ? `R${(n / 1e6).toFixed(1)}m` : n >= 1e3 ? `R${Math.round(n / 1e3)}k` : `R${Math.round(n)}` }
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// A clickable, sortable column header. Shows ▲/▼ on the active column, a faint ↕ otherwise.
function SortTh({ label, k, sort, onSort, align }) {
  const active = sort.key === k
  return (
    <th className={cn('px-2 py-3 font-semibold', align === 'right' && 'text-right')}>
      <button type="button" onClick={() => onSort(k)}
        className={cn('inline-flex items-center gap-1 uppercase tracking-wide hover:text-brand-ink', active ? 'text-brand-ink' : '')}
        title={`Sort by ${label}`}>
        {label}
        <span className={cn('text-[10px]', active ? 'text-brand-primary' : 'opacity-30')}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  )
}

// Inline editor row: set the not-booked reason + follow-up details, or log a follow-up. Submits a
// plain Inertia PATCH to /estimates/:id/tracking and reloads the list (Paul 2026-06-26).
function TrackEditor({ estimate, reasons, colSpan, onClose }) {
  const [reason, setReason] = useState(estimate.not_booked_reason || '')
  const [note, setNote] = useState(estimate.not_booked_reason_note || '')
  const [followUp, setFollowUp] = useState((estimate.next_follow_up_at || '').slice(0, 10))
  const [updateNote, setUpdateNote] = useState(estimate.follow_up_notes || '')
  const updateBox = useRef(null)
  // tri-state Yes / No / not recorded — mirrors the Excel "does the patient know the next treatment?"
  const [knows, setKnows] = useState(
    estimate.patient_knows_next_treatment === true ? 'true'
    : estimate.patient_knows_next_treatment === false ? 'false' : '')
  const [stamp, setStamp] = useState(true) // most edits ARE a follow-up → stamp last-follow-up = today
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const timer = useRef(null)
  const mounted = useRef(false)

  // Persist WITHOUT reloading the whole page — partial reload of just the estimates prop, preserving
  // component state so this editor stays open as you type. Used by both auto-save and close.
  const save = (opts = {}) => {
    setSaving(true)
    router.patch(`/estimates/${estimate.id}/tracking`, {
      tracking: {
        not_booked_reason: reason,
        not_booked_reason_note: note,
        next_follow_up_at: followUp,
        follow_up_notes: updateNote,
        patient_knows_next_treatment: knows,
        logged_follow_up: stamp,
      },
    }, {
      preserveScroll: true, preserveState: true, only: [ 'estimates', 'stats' ],
      onSuccess: () => { setSavedAt(new Date()); if (opts.close) onClose() },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish: () => setSaving(false),
    })
  }

  // Auto-save: debounce 800ms after the last change. Skips the initial mount so opening the row
  // doesn't fire a save (Paul 2026-07-01 — "when I complete this it should save automatically").
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => save(), 800)
    return () => clearTimeout(timer.current)
  }, [ reason, note, followUp, updateNote, knows ])
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <tr className="bg-brand-surface/40">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="text-xs font-medium text-brand-muted md:col-span-2">Update note — last communication + procedure done + outstanding
            <textarea ref={updateBox} value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} rows={2} placeholder="e.g. WA sent 26 Jun — fillings done, crowns outstanding, will book after bonus"
              className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
            <DictateButton targetRef={updateBox} onText={setUpdateNote} context="estimate_note" className="mt-1" />
          </label>
          <label className="text-xs font-medium text-brand-muted">Patient knows the next treatment?
            <select value={knows} onChange={(e) => setKnows(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
              <option value="">— not recorded —</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
          <label className="text-xs font-medium text-brand-muted">Reason not booked
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
              <option value="">— none —</option>
              {Object.entries(reasons).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-brand-muted">Reason detail
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. waiting for year-end bonus" className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs font-medium text-brand-muted">Next follow-up
            <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
          </label>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => { clearTimeout(timer.current); save({ close: true }) }} className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">Done</button>
          <span className="text-xs font-medium" aria-live="polite">
            {saving
              ? <span className="text-brand-muted">Saving…</span>
              : savedAt
              ? <span className="text-emerald-600">✓ Saved automatically</span>
              : <span className="text-brand-muted">Changes save automatically</span>}
          </span>
          <label className="flex items-center gap-1.5 text-xs text-brand-muted">
            <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} className="rounded border-brand-border" />
            Mark followed up today
          </label>
          {estimate.last_follow_up_at && <span className="text-xs text-brand-muted">Last: {fmtDate(estimate.last_follow_up_at)}</span>}
        </div>
      </td>
    </tr>
  )
}


// PATIENT-ROW editor (Paul 2026-09-09): one update note / reason / next follow-up written ONCE for every
// outstanding estimate of the patient shown in this tab — so reception updates the wording where they
// read it, instead of opening each estimate. Same auto-save rhythm as TrackEditor.
function PatientNoteEditor({ group, reasons, colSpan, onClose }) {
  const seed = group.estimates.find((e) => e.follow_up_notes || e.not_booked_reason) || group.estimates[0] || {}
  const targetIds = group.estimates.filter((e) => e.state !== 'done').map((e) => e.id)
  const [reason, setReason] = useState(seed.not_booked_reason || '')
  const [note, setNote] = useState(seed.not_booked_reason_note || '')
  const [followUp, setFollowUp] = useState((seed.next_follow_up_at || '').slice(0, 10))
  const [updateNote, setUpdateNote] = useState(seed.follow_up_notes || '')
  const updateBox = useRef(null)
  const [stamp, setStamp] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const timer = useRef(null)
  const mounted = useRef(false)
  const save = (opts = {}) => {
    setSaving(true)
    router.patch('/estimates/patient_tracking', {
      patient_id: group.patient_id, estimate_ids: targetIds,
      tracking: { not_booked_reason: reason, not_booked_reason_note: note, next_follow_up_at: followUp, follow_up_notes: updateNote, logged_follow_up: stamp },
    }, {
      preserveScroll: true, preserveState: true, only: [ 'estimates', 'stats' ],
      onSuccess: () => { setSavedAt(new Date()); if (opts.close) onClose() },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not save'),
      onFinish: () => setSaving(false),
    })
  }
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return }
    clearTimeout(timer.current)
    timer.current = setTimeout(() => save(), 800)
    return () => clearTimeout(timer.current)
  }, [ reason, note, followUp, updateNote ])
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <tr className="bg-brand-surface/40">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="mb-2 text-xs font-semibold text-brand-ink">Update for {group.patient_name} <span className="font-normal text-brand-muted">— applies to {targetIds.length} outstanding estimate{targetIds.length === 1 ? '' : 's'}</span></div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="text-xs font-medium text-brand-muted md:col-span-2">Update note — last communication + what is done + what is outstanding
            <textarea ref={updateBox} value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} rows={2} placeholder="e.g. Spoke 9 Sep — will book the crowns after month-end"
              className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
            <DictateButton targetRef={updateBox} onText={setUpdateNote} context="estimate_note" className="mt-1" />
          </label>
          <label className="text-xs font-medium text-brand-muted">Next follow-up
            <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs font-medium text-brand-muted">Reason not booked
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
              <option value="">— none —</option>
              {Object.entries(reasons).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-brand-muted md:col-span-2">Reason detail
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. waiting for year-end bonus" className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm" />
          </label>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => { clearTimeout(timer.current); save({ close: true }) }} className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-primary/90">Save &amp; close</button>
          <span className="text-xs font-medium" aria-live="polite">
            {saving ? <span className="text-brand-muted">Saving…</span> : savedAt ? <span className="text-emerald-600">✓ Saved automatically</span> : <span className="text-brand-muted">Changes save automatically</span>}
          </span>
          <label className="flex items-center gap-1.5 text-xs text-brand-muted">
            <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} className="rounded border-brand-border" />
            Mark followed up today
          </label>
        </div>
      </td>
    </tr>
  )
}

// ONE ROW PER PATIENT (Paul 2026-09-09): the tracker used to list every estimate as its own row, so a
// patient with five visits appeared five times. Group the (already filtered + sorted) estimates by
// patient, keeping the order of each patient's first appearance so the column sort still drives it.
function groupByPatient(rows) {
  const map = new Map()
  rows.forEach((e) => {
    if (!map.has(e.patient_id)) {
      map.set(e.patient_id, { patient_id: e.patient_id, patient_name: e.patient_name, account_code: e.account_code, phone: e.phone, estimates: [] })
    }
    map.get(e.patient_id).estimates.push(e)
  })
  return [...map.values()].map((g) => {
    const es = g.estimates
    const withReply = es.find((e) => e.last_reply_at)
    const withVisit = es.find((e) => e.last_visit_signal)
    const withCall = es.find((e) => e.last_call)
    const nextAny = es.find((e) => e.patient_next_appointment)?.patient_next_appointment || null
    const appts = es.map((e) => e.appointment_at).filter(Boolean).sort()
    return {
      ...g,
      total: es.reduce((s, e) => s + (e.total || 0), 0),
      states: es.reduce((m, e) => { m[e.state] = (m[e.state] || 0) + 1; return m }, {}),
      ai: es.some((e) => e.ai && e.status !== 'accepted'),
      last_reply_at: withReply?.last_reply_at, last_reply_text: withReply?.last_reply_text,
      last_reply_via: withReply?.last_reply_via,
      next_action: es.map((e) => e.next_action).find(Boolean),
      conversation_id: withReply?.conversation_id, replied_since_shared: es.some((e) => e.replied_since_shared),
      awaiting_reply: es.some((e) => e.awaiting_reply),
      last_visit_at: es.map((e) => e.last_visit_at).filter(Boolean).sort().pop() || null,
      last_visit_signal: withVisit?.last_visit_signal || null,
      last_call: withCall?.last_call || null,
      note: es.map((e) => e.follow_up_notes).find(Boolean) || null,
      reason_label: es.map((e) => e.not_booked_reason_label).find(Boolean) || null,
      next_appointment_at: appts[0] || null,
      // any upcoming booking, even for other work — shown as "other booking" when no estimate is linked to it
      other_booking: nextAny && !appts.length ? nextAny : null,
      bookable: es.find((e) => e.state !== 'done' && e.state !== 'booked' && e.state !== 'blocked') || null,
    }
  })
}

// A phone number as something you can act on from the row: tap-to-call + WhatsApp (Paul 2026-09-09).
function PhoneLinks({ phone }) {
  if (!phone) return <span className="text-[11px] text-brand-muted">no number</span>
  const wa = phone.replace(/[^0-9]/g, '')
  return (
    <span className="inline-flex items-center gap-2 text-[11px]">
      <a href={`tel:${phone}`} className="inline-flex items-center gap-1 font-mono text-brand-ink hover:text-brand-primary hover:underline" title="Call this number"><Phone size={11} /> {phone}</a>
      <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline" title="Open in WhatsApp"><MessageCircle size={11} /> WhatsApp</a>
    </span>
  )
}

// What should happen next for this patient, written by Claude from everything the practice knows:
// both WhatsApp lines, the transcribed phone calls and the chair-side scribe's summary of the visit.
//
// It is a SUGGESTION and is labelled as one. Reception's own note lives in its own field and is never
// overwritten — the point is to save someone reading four conversations to work out that a quote was
// never sent, not to decide for them.
const NA_TONE = {
  call:          'bg-violet-50 text-violet-900 border-violet-200',
  send_estimate: 'bg-amber-50 text-amber-900 border-amber-200',
  new_estimate:  'bg-amber-50 text-amber-900 border-amber-200',
  book:          'bg-emerald-50 text-emerald-900 border-emerald-200',
  wait:          'bg-slate-50 text-slate-600 border-slate-200',
  none:          'bg-slate-50 text-slate-500 border-slate-200',
}

function NextActionCard({ na }) {
  const [open, setOpen] = useState(false)
  const tone = NA_TONE[na.kind] || 'bg-slate-50 text-slate-700 border-slate-200'
  const overdue = na.due_on && new Date(na.due_on) < new Date(new Date().toDateString())
  return (
    <div className={cn('rounded-md border px-2 py-1.5 text-[11px] leading-snug', tone)}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-1.5 text-left">
        <Sparkles size={11} className="mt-0.5 shrink-0 opacity-70" />
        <span className="min-w-0 flex-1">
          <span className="font-semibold">{na.label}</span>
          {na.due_on && (
            <span className={cn('ml-1.5 rounded-full px-1.5 py-px text-[10px] font-semibold', overdue ? 'bg-red-100 text-red-700' : 'bg-white/70')}>
              {overdue ? 'due ' : 'by '}{fmtDate(na.due_on)}
            </span>
          )}
          {na.next_step && <span className="ml-1 opacity-90">— {na.next_step}</span>}
        </span>
      </button>
      {open && (
        <div className="mt-1.5 border-t border-current/15 pt-1.5 opacity-90">
          {na.summary && <p className="mb-1">{na.summary}</p>}
          {na.evidence?.length > 0 && (
            <ul className="list-disc pl-4">{na.evidence.map((x, i) => <li key={i}>{x}</li>)}</ul>
          )}
          <p className="mt-1 text-[10px] opacity-60">
            Suggested by Claude{na.confidence ? ` · ${na.confidence} confidence` : ''} — reception decides.
          </p>
        </div>
      )}
    </div>
  )
}

export default function Estimates({ estimates = [], stats = {}, reasons = {}, providers = [], q = '' }) {
  const [filter, setFilter] = useState('outstanding') // Outstanding is the daily worklist → open there
  const [query, setQuery] = useState(q || '')
  // SEARCH (Paul 2026-09-09): 2+ characters → ask the server for EVERY matching estimate (any status, whole
  // history: name / account number / estimate number / phone) and show them across all tabs; clearing the
  // box returns to the normal worklist. Debounced so typing doesn't hammer the server.
  const searchTimer = useRef(null)
  const searching = query.trim().length >= 2
  useEffect(() => {
    clearTimeout(searchTimer.current)
    const term = query.trim()
    if (term === (q || '')) return
    if (term.length >= 2 || (term.length === 0 && q)) {
      searchTimer.current = setTimeout(() => router.get('/estimates', term ? { q: term } : {}, { preserveState: true, preserveScroll: true, replace: true, only: [ 'estimates', 'stats', 'q' ] }), 350)
    }
    return () => clearTimeout(searchTimer.current)
  }, [query])
  const [openId, setOpenId] = useState(null)
  const [collapsedPatients, setCollapsedPatients] = useState(() => new Set()) // rows are OPEN by default (Paul 2026-09-09); this tracks the ones folded away
  // 🚨 RENDER IN PAGES (Paul 2026-09-11: "when I open it my PC becomes very slow").
  //
  // Every patient row is expanded by default, which is what Paul asked for — but with 197 patients and
  // 467 estimates that put THIRTY-TWO THOUSAND nodes in the DOM on first paint, and a browser starts
  // to labour well before that. Paging the groups keeps the open-by-default behaviour he wanted while
  // only building what he can actually see; the filters and the search box are how he reaches the rest.
  const GROUP_PAGE = 40
  const [visibleGroups, setVisibleGroups] = useState(GROUP_PAGE)
  const [noteFor, setNoteFor] = useState(null) // patient_id whose patient-level note editor is open
  const togglePatient = (pid) => setCollapsedPatients((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n })
  // DELAY buttons (Paul 2026-09-09): "let it appear here in 2 days / 7 days / 2 weeks / 4 weeks / 3 months" — sets the next
  // follow-up on every outstanding estimate of the patient, which moves them to Contact later until that date.
  const DELAYS = [ ['2d', 2, '2 days'], ['7d', 7, '7 days'], ['2w', 14, '2 weeks'], ['4w', 28, '4 weeks'], ['3m', 91, '3 months'] ]
  const delayPatient = (g, days, label) => {
    const d = new Date(); d.setDate(d.getDate() + days)
    const iso = d.toISOString().slice(0, 10)
    const ids = g.estimates.filter((e) => e.state !== 'done').map((e) => e.id)
    router.patch('/estimates/patient_tracking', { patient_id: g.patient_id, estimate_ids: ids, tracking: { next_follow_up_at: iso, logged_follow_up: true } }, {
      preserveScroll: true, only: ['estimates', 'stats'],
      onSuccess: () => toast.success(`${g.patient_name} → Contact later, back on ${fmtDate(iso)} (${label})`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not delay'),
    })
  }
  const [menu, setMenu] = useState(null) // right-click context menu: { x, y, e }
  const [bookEstimate, setBookEstimate] = useState(null) // "Book" → opens the appointment modal, hard-linked to this estimate
  const openBooking = (e) => setBookEstimate({
    id: e.id, title: e.details || e.number,
    patient: { id: e.patient_id, full_name: e.patient_name, name: e.patient_name, account_code: e.account_code },
  })
  const [sort, setSort] = useState({ key: 'last_activity', dir: 'desc' })
  // Click a heading to sort by it; click again to flip direction. Numbers/dates high→low by default.
  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' })
  const COLS = 8

  // Keep the tracker fresh: re-pull estimates + stats every 2 min so a patient's new reply / booking /
  // completion shows without a manual refresh (preserveState so open editors + sort aren't disturbed).
  useEffect(() => {
    const id = setInterval(() => router.reload({ only: ['estimates', 'stats'], preserveScroll: true, preserveState: true }), 120000)
    return () => clearInterval(id)
  }, [])

  const knowsPill = (v) => v === true
    ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">Yes</span>
    : v === false
    ? <span className="rounded bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700">No</span>
    : <span className="text-brand-muted">—</span>

  // Close the context menu on any outside click / escape / scroll.
  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    window.addEventListener('click', close)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', close)
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', close) }
  }, [menu])

  const deleteEstimate = (e) => {
    if (!window.confirm(`Delete estimate ${e.number} for ${e.patient_name}? This removes it permanently.`)) return
    router.delete(`/estimates/${e.id}`, {
      preserveScroll: true, only: ['estimates', 'stats'],
      onSuccess: () => toast.success(`Deleted ${e.number}`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not delete'),
    })
  }
  const cancelEstimate = (e) => router.post(`/estimates/${e.id}/cancel`, {}, {
    preserveScroll: true, only: ['estimates', 'stats'],
    onSuccess: () => toast.success(`${e.number} removed from the tracker`),
  })
  // Manual "patient has booked" override → moves it to Booked; server links the soonest appt for the date.
  const markBooked = (e) => router.post(`/estimates/${e.id}/mark_booked`, {}, {
    preserveScroll: true, only: ['estimates', 'stats'],
    onSuccess: (page) => toast.success(page?.props?.flash?.notice || `${e.number} marked as booked`),
    onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not mark as booked'),
  })
  const unmarkBooked = (e) => router.post(`/estimates/${e.id}/unmark_booked`, {}, {
    preserveScroll: true, only: ['estimates', 'stats'],
    onSuccess: () => toast.success(`${e.number} moved back to the worklist`),
  })
  // One-click snooze → moves the estimate to "Contact later" for N days (Paul 2026-07-02).
  const snoozeEstimate = (e, days = 14) => {
    const d = new Date(); d.setDate(d.getDate() + days)
    const iso = d.toISOString().slice(0, 10)
    router.patch(`/estimates/${e.id}/tracking`, { tracking: { next_follow_up_at: iso, logged_follow_up: true } }, {
      preserveScroll: true, only: ['estimates', 'stats'],
      onSuccess: () => toast.success(`${e.number} → Contact later (${fmtDate(iso)})`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not snooze'),
    })
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return estimates.filter((e) => {
      const matchQ = !q || `${e.patient_name} ${e.number} ${e.account_code || ''}`.toLowerCase().includes(q)
      if (!matchQ) return false
      // Blocked (later visits the patient can't do yet) only ever appear under All — never on a working tab.
      if (filter === 'all' || searching) return true // a search shows every status, so nothing is hidden by the tab
      if (e.state === 'blocked') return false
      if (filter === 'outstanding') return e.state === 'outstanding'
      if (filter === 'contact_later') return e.state === 'snoozed'
      if (filter === 'booked') return e.state === 'booked'
      if (filter === 'done') return e.state === 'done'
      return true
    })
  }, [estimates, filter, query])

  // Sort by the clicked column. Value = numeric (biggest→lowest to target the money); dates sort
  // chronologically (ISO strings compare correctly); text sorts alphabetically.
  const sorted = useMemo(() => {
    const val = (e) => {
      switch (sort.key) {
        case 'value': return e.total || 0
        case 'date_sent': return e.date_sent || ''
        case 'last_visit': return e.last_visit_at || ''
        case 'patient': return (e.patient_name || '').toLowerCase()
        case 'status': return e.state || ''
        case 'treatment': return (e.details || '').toLowerCase()
        case 'last_follow_up': return e.last_follow_up_at || ''
        case 'next_follow_up': return e.next_follow_up_at || ''
        case 'appointment': return e.appointment_at || ''
        default: return e.last_activity || ''
      }
    }
    const rows = [...filtered]
    rows.sort((a, b) => {
      const av = val(a), bv = val(b)
      if (av < bv) return sort.dir === 'asc' ? -1 : 1
      if (av > bv) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return rows
  }, [filtered, sort])

  const groups = useMemo(() => groupByPatient(sorted), [sorted])
  // Changing the tab, the sort or the search rebuilds the list, so start again from the first page —
  // otherwise a filter down to three patients would still be sitting on "40 shown".
  useEffect(() => { setVisibleGroups(GROUP_PAGE) }, [groups.length])

  const TABS = [
    { key: 'outstanding',   label: 'Outstanding',   count: stats.outstanding ?? 0,   value: stats.outstanding_value, dot: 'bg-amber-400' },
    { key: 'contact_later', label: 'Contact later', count: stats.contact_later ?? 0, dot: 'bg-violet-400' },
    { key: 'booked',        label: 'Booked',        count: stats.booked ?? 0,        value: stats.booked_value,      dot: 'bg-blue-500' },
    { key: 'done',          label: 'Done',          count: stats.done ?? 0,          value: stats.done_value,        dot: 'bg-emerald-500' },
    { key: 'all',           label: 'All',           count: stats.total ?? estimates.length, dot: null },
  ]

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <FileText size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Estimates tracker</h1>
          <p className="text-sm text-brand-muted">Every estimate, the work quoted, whether an appointment is booked — and why not, so you can follow up.</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setFilter(t.key)}
            className={cn('inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition',
              filter === t.key ? 'border-brand-primary bg-brand-primary/5 text-brand-ink' : 'border-brand-border bg-white text-brand-muted hover:border-brand-primary')}>
            {t.dot && <span className={cn('h-2 w-2 rounded-full', t.dot)} />}
            {t.label} <span className="text-xs text-brand-muted">({t.count}{t.value != null ? ` · ${randK(t.value)}` : ''})</span>
          </button>
        ))}
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search patient, account no., estimate no., phone…"
          className="ml-1 w-60 rounded-lg border border-brand-border px-3 py-1.5 text-sm focus:border-brand-primary focus:outline-none" />
        <a href="/estimates.csv" className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-3 py-1.5 text-sm font-medium text-brand-muted transition hover:border-brand-primary hover:text-brand-ink">
          <Download size={14} /> Export CSV
        </a>
      </div>

      {(stats.aged ?? 0) > 0 && (
        <button type="button" onClick={() => setFilter('outstanding')}
          className="mb-3 flex w-full items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm text-amber-800 hover:bg-amber-100">
          <span className="font-semibold">{stats.aged} overdue follow-up{stats.aged === 1 ? '' : 's'}</span>
          <span className="text-amber-700">— outstanding estimates older than 30 days{stats.aged_value ? ` · ${randK(stats.aged_value)}` : ''}. Worth a call.</span>
        </button>
      )}

      {sorted.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brand-border bg-white px-6 py-12 text-center text-sm text-brand-muted">No estimates in this view.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-brand-border bg-white">
          <div className="flex items-center gap-2 border-b border-brand-border bg-brand-surface/60 px-3 py-1.5 text-[11px] text-brand-muted"><Users size={12} /> {groups.length} patient{groups.length === 1 ? '' : 's'} · {sorted.length} estimate{sorted.length === 1 ? '' : 's'}{searching ? <span className="text-brand-ink"> — every status, whole history, matching {JSON.stringify(query.trim())}</span> : ' — click a name to expand their estimates'}</div>
          <table className="w-full text-sm">
            <thead className="border-b border-brand-border bg-brand-surface text-left text-xs uppercase tracking-wide text-brand-muted">
              <tr>
                <SortTh label="Status"      k="status"        sort={sort} onSort={toggleSort} />
                <SortTh label="Patient"     k="patient"       sort={sort} onSort={toggleSort} />{/* bounded width — the signals wrap inside it (Paul 2026-09-09: fit the screen) */}
                <SortTh label="Sent"        k="date_sent"     sort={sort} onSort={toggleSort} />
                <SortTh label="Last visit"  k="last_visit"    sort={sort} onSort={toggleSort} />
                <SortTh label="Treatment"   k="treatment"     sort={sort} onSort={toggleSort} />
                <SortTh label="Value"       k="value"         sort={sort} onSort={toggleSort} align="right" />
                <SortTh label="Appointment" k="appointment"   sort={sort} onSort={toggleSort} />
                <th className="px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {groups.slice(0, visibleGroups).map((g) => {
                const pOpen = !collapsedPatients.has(g.patient_id)
                const outstanding = g.states.outstanding || 0
                return (
                  <React.Fragment key={`p-${g.patient_id}`}>
                    <tr className={cn('border-b border-brand-border/60 hover:bg-brand-surface/50', outstanding > 0 ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-transparent', pOpen && 'bg-brand-surface/30')}>
                      <td className="px-3 py-2.5 whitespace-nowrap align-top">
                        <div className="flex flex-col gap-1">
                          {Object.entries(g.states).map(([st, n]) => {
                            const col = st === 'done' ? 'green' : st === 'booked' ? 'blue' : st === 'snoozed' ? 'grey' : st === 'blocked' ? 'slate' : 'yellow'
                            const lc = LIFECYCLE[col]
                            return <span key={st} className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium', lc.pill)}><span className={cn('h-2 w-2 rounded-full', lc.dot)} />{n > 1 ? `${n} ` : ''}{lc.label}</span>
                          })}
                        </div>
                      </td>
                      <td className="w-[34rem] max-w-[34rem] px-3 py-2.5 align-top">
                        <button type="button" onClick={() => togglePatient(g.patient_id)} className="inline-flex items-center gap-1 text-left font-medium text-brand-ink hover:text-brand-primary" title={pOpen ? 'Collapse' : `Show ${g.estimates.length} estimate${g.estimates.length === 1 ? '' : 's'}`}>
                          <ChevronDown size={13} className={cn('shrink-0 transition', pOpen && 'rotate-180')} />{g.patient_name}
                          <span className="ml-1 rounded bg-brand-surface px-1.5 py-0.5 text-[10px] font-semibold text-brand-muted">{g.estimates.length}</span>
                        </button>
                        {g.ai && <div className="mt-0.5 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">⚠ AI estimated — review</div>}
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {g.account_code && <Link href={`/patients/${g.patient_id}`} className="font-mono text-[11px] text-brand-muted hover:underline" title="Open patient">{g.account_code}</Link>}
                          <PhoneLinks phone={g.phone} />
                        </div>
                        <div className="mt-1 flex max-w-[26rem] flex-col gap-0.5">
                          {g.next_action && <NextActionCard na={g.next_action} />}
                          {g.last_reply_at
                            ? <Link href={g.conversation_id ? `/conversations?${g.last_reply_via === '071' ? 'channel=whatsapp_live&' : ''}selected_id=${g.conversation_id}` : `/patients/${g.patient_id}`}
                                className={cn('inline-flex items-start gap-1 rounded px-1.5 py-0.5 text-[11px]', g.replied_since_shared ? 'bg-blue-50 text-blue-800 hover:bg-blue-100' : 'bg-brand-surface text-brand-ink hover:bg-brand-surface/70')}
                                title={g.last_reply_via === '071'
                                  ? 'Latest message from the patient — on the 071 practice phone. Click to open that thread.'
                                  : 'Latest WhatsApp message from the patient — click to open the conversation'}>
                                <MessageCircle size={11} className="mt-0.5 shrink-0" /><span>
                                  {/* Which line they answered on. Without it a 071 reply reads as an AI-line
                                      reply, and reception tries to answer somewhere the patient is not. */}
                                  {g.last_reply_via === '071' && <span className="mr-1 rounded-full bg-brand-accent/30 px-1 font-semibold">071</span>}
                                  <span className="font-medium">{fmtDate(g.last_reply_at)}:</span> <span className="italic">“{g.last_reply_text || '…'}”</span></span>
                              </Link>
                            : g.awaiting_reply && <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800"><MessageCircle size={11} /> Sent — no reply yet</span>}
                          {g.last_call && (
                            <span className="inline-flex items-start gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[11px] text-violet-900" title={`Last phone call (${g.last_call.direction === 'out' ? 'we called' : 'they called'})${g.last_call.intent ? ` · ${g.last_call.intent}` : ''}`}>
                              <PhoneCall size={11} className="mt-0.5 shrink-0" /><span><span className="font-medium">Call {fmtDate(g.last_call.at)}{g.last_call.direction === 'out' ? ' (out)' : ''}:</span> {g.last_call.intent && <span className="mr-1 rounded bg-violet-100 px-1 text-[10px] font-medium">{g.last_call.intent}</span>}<span className="italic">{g.last_call.text || '…'}</span></span>
                            </span>
                          )}
                          {g.last_visit_signal && (
                            <Link href={`/appointments/${g.last_visit_signal.appointment_id}`} className="inline-flex items-start gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-900 hover:bg-emerald-100" title="What the last visit recorded — click to open the visit">
                              <Stethoscope size={11} className="mt-0.5 shrink-0" /><span><span className="font-medium">Visit {fmtDate(g.last_visit_signal.at)}:</span> {g.last_visit_signal.text}</span>
                            </Link>
                          )}
                          {outstanding > 0 && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-brand-muted">
                              <span className="mr-0.5">Delay:</span>
                              {DELAYS.map(([k, days, label]) => (
                                <button key={k} type="button" onClick={() => delayPatient(g, days, label)}
                                  className="rounded border border-brand-border bg-white px-1.5 py-0.5 font-medium text-brand-ink hover:border-violet-400 hover:bg-violet-50 hover:text-violet-800"
                                  title={`Contact later — bring this patient back in ${label}`}>{label}</button>
                              ))}
                            </div>
                          )}
                          {(g.note || g.reason_label) && (
                            <button type="button" onClick={() => setNoteFor(noteFor === g.patient_id ? null : g.patient_id)} className="inline-flex items-start gap-1 rounded px-1.5 py-0.5 text-left text-[11px] text-brand-muted hover:bg-brand-surface" title="Our last note — click to update">
                              <Pencil size={11} className="mt-0.5 shrink-0" /><span>{g.reason_label && <span className="mr-1 rounded bg-rose-50 px-1 text-[10px] font-medium text-rose-700">{g.reason_label}</span>}{g.note}</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-brand-muted">{fmtDate(g.estimates.map((e) => e.date_sent).filter(Boolean).sort().pop())}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-brand-muted">{g.last_visit_at ? fmtDate(g.last_visit_at) : '—'}</td>
                      <td className="px-2 py-2.5 max-w-[15rem] align-top text-brand-ink">
                        {g.estimates.slice(0, 3).map((e) => <div key={e.id} className="truncate" title={e.details || ''}>{e.details || e.number}</div>)}
                        {g.estimates.length > 3 && <div className="text-[11px] text-brand-muted">+{g.estimates.length - 3} more</div>}
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-right font-medium text-brand-ink">{rand(g.total)}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top">
                        {g.next_appointment_at
                          ? <span className="inline-flex items-center gap-1 text-blue-700" title="Booked for this work"><CalendarCheck size={13} /> {fmtDate(g.next_appointment_at)}</span>
                          : g.other_booking
                          ? <span className="inline-flex flex-col text-amber-800" title={`Has a booking for OTHER work${g.other_booking.provider_name ? ` with ${g.other_booking.provider_name}` : ''} — the estimate itself is still unbooked`}>
                              <span className="inline-flex items-center gap-1"><CalendarCheck size={13} /> {fmtDate(g.other_booking.at)}</span>
                              <span className="max-w-[9rem] truncate text-[10px]">other booking{g.other_booking.reason ? `: ${g.other_booking.reason}` : ''}</span>
                            </span>
                          : <span className="text-brand-muted">—</span>}
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap text-right align-top">
                        {g.bookable && (
                          <button type="button" onClick={() => openBooking(g.bookable)} className="mr-1.5 inline-flex items-center gap-0.5 text-brand-primary hover:underline" title={`Book an appointment for ${g.bookable.number}`}>
                            <CalendarPlus size={13} /> Book
                          </button>
                        )}
                        <button type="button" onClick={() => setNoteFor(noteFor === g.patient_id ? null : g.patient_id)} className="mr-1.5 inline-flex items-center gap-0.5 text-brand-muted hover:text-brand-primary" title="Update the note / reason for all this patient's outstanding estimates">
                          <Pencil size={13} />
                        </button>
                        <button type="button" onClick={() => togglePatient(g.patient_id)} className="inline-flex items-center text-brand-muted hover:text-brand-primary" title={pOpen ? 'Collapse' : 'Expand'}>
                          <ChevronDown size={14} className={cn('transition', pOpen && 'rotate-180')} />
                        </button>
                      </td>
                    </tr>
                    {noteFor === g.patient_id && <PatientNoteEditor group={g} reasons={reasons} colSpan={COLS} onClose={() => setNoteFor(null)} />}
                    {pOpen && g.estimates.map((e) => {
                      const lc = LIFECYCLE[e.status_colour] || LIFECYCLE.yellow
                      const isOpen = openId === e.id
                    return (
                  <React.Fragment key={e.id}>
                    <tr onContextMenu={(ev) => { ev.preventDefault(); setMenu({ x: ev.clientX, y: ev.clientY, e }) }}
                      className={cn('border-b border-brand-border/60 bg-brand-surface/20 hover:bg-brand-surface/50', e.state === 'outstanding' ? 'border-l-4 border-l-amber-400' : e.state === 'blocked' && 'opacity-60', isOpen && 'bg-brand-surface/30')}>
                      <td className="px-3 py-2.5 whitespace-nowrap align-top">
                        <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium', lc.pill)}>
                          <span className={cn('h-2 w-2 rounded-full', lc.dot)} />{lc.label}
                        </span>
                      </td>
                      <td className="max-w-[34rem] px-3 py-2.5 align-top">
                        <Link href={`/estimates/${e.id}`} className="block truncate font-medium text-brand-ink hover:text-brand-primary hover:underline" title={`Open estimate ${e.number}: ${e.details || ''}`}>{e.details || e.number}</Link>
                        {e.ai && e.status !== 'accepted' && <div className="mt-0.5 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">⚠ AI estimated — review</div>}
                        
                        <div className="mt-1 flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-1 whitespace-nowrap">
                            {e.shared_at && <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700" title={`Sent to the patient on WhatsApp on ${fmtDate(e.shared_at)}`}>📱 Sent · {fmtDate(e.shared_at)}</span>}
                            {e.awaiting_reply && <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" title="Sent, but the patient hasn't replied since — worth a chase">⏳ No response yet</span>}
                            {e.not_booked_reason_label && <span className="inline-flex items-center rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700" title="Reason not booked (set under Track)">{e.not_booked_reason_label}</span>}
                          </div>
                          {e.last_reply_at && (
                            <Link href={e.conversation_id ? `/conversations?${e.last_reply_via === '071' ? 'channel=whatsapp_live&' : ''}selected_id=${e.conversation_id}` : `/patients/${e.patient_id}`}
                              className={cn('group inline-flex max-w-[240px] items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium', e.replied_since_shared ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-brand-surface text-brand-muted hover:bg-brand-border/40')}
                              title={e.last_reply_text ? `“${e.last_reply_text}” — click to open the conversation` : 'Open the conversation'}>
                              {e.last_reply_via === '071' ? '📱' : '💬'} {fmtDate(e.last_reply_at)}
                              {e.last_reply_text && <span className="truncate italic opacity-90">“{e.last_reply_text}”</span>}
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-brand-muted">{fmtDate(e.date_sent)}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-brand-muted" title={e.last_visit_at ? 'The last appointment this patient attended' : 'No past visit on record'}>{e.last_visit_at ? fmtDate(e.last_visit_at) : '—'}</td>
                      <td className="px-2 py-2.5 max-w-[15rem] align-top text-brand-ink">
                        <div className="truncate" title={e.details || ''}>{e.details || '—'}</div>
                        {e.provider_name && <div className="truncate text-[11px] text-brand-muted" title={e.provider_name}>{e.provider_name}</div>}
                        {e.completion && (
                          e.completion.review
                            ? <span className="mt-0.5 inline-block rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" title="Some billed work is on different teeth than planned — please review">⚠ review — {e.completion.covered}/{e.completion.total} done</span>
                            : <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" title="Part of this plan has already been invoiced">{e.completion.covered}/{e.completion.total} done ({e.completion.pct}%)</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top text-right font-medium text-brand-ink">{rand(e.total)}</td>
                      <td className="px-2 py-2.5 whitespace-nowrap align-top">
                        {e.appointment_at
                          ? <span className="inline-flex items-center gap-1 text-blue-700" title={e.appointment_reason || 'Booked appointment'}><CalendarCheck size={13} /> {fmtDate(e.appointment_at)}</span>
                          : <span className="text-brand-muted">—</span>}
                      </td>
                      <td className="px-2 py-2.5 whitespace-nowrap text-right align-top">
                        {e.state !== 'done' && e.state !== 'booked' && e.state !== 'blocked' && (
                          <button type="button" onClick={() => openBooking(e)} className="mr-1.5 inline-flex items-center gap-0.5 text-brand-primary hover:underline" title="Book an appointment for this estimate">
                            <CalendarPlus size={13} /> Book
                          </button>
                        )}
                        <button type="button" onClick={() => setOpenId(isOpen ? null : e.id)} className="mr-1.5 inline-flex items-center gap-0.5 text-brand-muted hover:text-brand-primary" title={e.follow_up_notes ? `Note: ${e.follow_up_notes}` : 'Set reason / log a follow-up'}>
                          <PhoneCall size={13} /><ChevronDown size={12} className={cn('transition', isOpen && 'rotate-180')} />
                        </button>
                        <Link href={`/estimates/${e.id}`} className="inline-flex items-center text-brand-primary hover:underline" title="Open estimate"><ChevronRight size={15} /></Link>
                      </td>
                    </tr>
                    {isOpen && <TrackEditor estimate={e} reasons={reasons} colSpan={COLS} onClose={() => setOpenId(null)} />}
                  </React.Fragment>
                    )
                    })}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
          {groups.length > visibleGroups && (
            <div className="border-t border-brand-border bg-brand-surface/40 px-3 py-3 text-center">
              <button type="button"
                onClick={() => setVisibleGroups((n) => n + GROUP_PAGE)}
                className="rounded-lg border border-brand-border bg-white px-4 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-surface">
                Show {Math.min(GROUP_PAGE, groups.length - visibleGroups)} more patients
                <span className="ml-1.5 font-normal text-brand-muted">({visibleGroups} of {groups.length} shown)</span>
              </button>
              <p className="mt-1.5 text-[11px] text-brand-muted">
                Searching or changing the filter above looks across all {groups.length}, not just the ones on screen.
              </p>
            </div>
          )}
        </div>
      )}

      {menu && (
        <div className="fixed z-50 min-w-[200px] overflow-hidden rounded-lg border border-brand-border bg-white py-1 shadow-lg"
          style={{ top: Math.min(menu.y, window.innerHeight - 160), left: Math.min(menu.x, window.innerWidth - 220) }}
          onClick={(ev) => ev.stopPropagation()}>
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-muted">{menu.e.number}</div>
          <button type="button" onClick={() => { openBooking(menu.e); setMenu(null) }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-primary hover:bg-brand-surface"><CalendarPlus size={14} /> Book appointment</button>
          {menu.e.status === 'booked'
            ? <button type="button" onClick={() => { unmarkBooked(menu.e); setMenu(null) }}
                className="block w-full px-3 py-2 text-left text-sm text-brand-ink hover:bg-brand-surface">↩ Unmark booked (back to worklist)</button>
            : <button type="button" onClick={() => { markBooked(menu.e); setMenu(null) }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-brand-surface"><CalendarCheck size={14} /> Patient has booked</button>}
          <Link href={`/estimates/${menu.e.id}`} className="block px-3 py-2 text-sm text-brand-ink hover:bg-brand-surface">Open estimate</Link>
          <button type="button" onClick={() => { snoozeEstimate(menu.e, 14); setMenu(null) }}
            className="block w-full px-3 py-2 text-left text-sm text-brand-ink hover:bg-brand-surface">💤 Contact later (snooze 2 weeks)</button>
          <button type="button" onClick={() => { cancelEstimate(menu.e); setMenu(null) }}
            className="block w-full px-3 py-2 text-left text-sm text-brand-ink hover:bg-brand-surface">Remove from tracker (cancel)</button>
          <button type="button" onClick={() => { deleteEstimate(menu.e); setMenu(null) }}
            className="block w-full px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50">Delete permanently</button>
        </div>
      )}

      <AppointmentFormModal
        mode="create"
        open={!!bookEstimate}
        onClose={() => setBookEstimate(null)}
        prefillEstimate={bookEstimate}
        providers={providers}
        patients={bookEstimate ? [bookEstimate.patient] : []}
      />
    </DashboardLayout>
  )
}
